/** Selling a liquid by the dose from an open bottle: the till's sheet, the product form, the menu switch. */
const doses = {
  en: {
    button: "Doses",
    title: "Sold by the dose",
    subtitle: "Tap an open bottle to add a dose to the ticket.",
    openBottles: "Open bottles",
    noneOpen: "No bottle is open. Open one below — it comes off the shelf.",
    dose: "dose",
    lineName: "{name} · dose",
    sold: { one: "{count} dose sold", other: "{count} doses sold" },
    perBottle: "about {count} per bottle",
    added: { one: "{count} on the ticket", other: "{count} on the ticket" },
    finish: "Bottle finished",
    finishConfirm: "Is the {name} bottle empty? {count} doses were sold from it.",
    cancel: "Put back",
    cancelConfirm: "Put the {name} bottle back on the shelf? Nothing was poured from it, so it goes back into stock.",
    summary: {
      title: "Bottle finished",
      doses: "Doses sold",
      lasted: "Lasted",
      minutes: { one: "{count} minute", other: "{count} minutes" },
      hours: { one: "{count} hour", other: "{count} hours" },
      days: { one: "{count} day", other: "{count} days" },
      revenue: "Brought in",
      cost: "The bottle cost",
      margin: "Left after the bottle",
      unknown: "unknown",
      note: "Refunded doses are not counted.",
      done: "OK",
    },
    finishing: "Closing…",
    openTitle: "Open a bottle",
    openHint: "One bottle comes off the shelf now. Doses are then sold from it until you mark it finished.",
    open: "Open",
    opening: "Opening…",
    left: { one: "{count} left", other: "{count} left" },
    noneLeft: "none left",
    noProducts: "No product is sold by the dose yet. On Products, press “Sell by the dose” to tick your liquids and give the price of a dose.",
    failed: "That did not work. Try again.",
    product: {
      toggle: "Also sold by the dose",
      hint: "For a liquid you pour into the customer's pod from an open bottle. Open a bottle at the till (it comes off the stock), then sell doses from it.",
      price: "Price of one dose",
      perBottle: "Doses per bottle so far: about {count}",
    },
    setup: {
      button: "Sell by the dose",
      title: "Liquids sold by the dose",
      intro: "Tick every liquid you pour by the dose and give the price of one dose, then save once. Unticking one keeps its price for next time.",
      search: "Search a liquid",
      priceForAll: "Dose price",
      applyToTicked: "Give it to all ticked",
      tickAll: "Tick all shown",
      untickAll: "Untick all shown",
      ticked: { one: "{count} ticked", other: "{count} ticked" },
      bottle: "Bottle: {price}",
      dosePrice: "Dose",
      empty: "No liquid in the shop yet.",
      missingPrice: { one: "{count} ticked liquid has no dose price", other: "{count} ticked liquids have no dose price" },
      save: { one: "Save {count} change", other: "Save {count} changes" },
      nothing: "Nothing to save",
      saving: "Saving…",
      saved: { one: "{count} liquid saved.", other: "{count} liquids saved." },
      failed: "That did not work. Try again.",
    },
    menu: {
      toggle: "Say on the menu which liquids are sold by the dose",
      hint: "Customers see “also by the dose” and the price of a dose on those products.",
    },
  },

  fr: {
    button: "Doses",
    title: "Vendu à la dose",
    subtitle: "Touchez une bouteille ouverte pour ajouter une dose au ticket.",
    openBottles: "Bouteilles ouvertes",
    noneOpen: "Aucune bouteille ouverte. Ouvrez-en une ci-dessous — elle sort du stock.",
    dose: "dose",
    lineName: "{name} · dose",
    sold: { one: "{count} dose vendue", other: "{count} doses vendues" },
    perBottle: "environ {count} par bouteille",
    added: { one: "{count} sur le ticket", other: "{count} sur le ticket" },
    finish: "Bouteille terminée",
    finishConfirm: "La bouteille de {name} est-elle vide ? {count} doses en ont été vendues.",
    cancel: "Remettre en stock",
    cancelConfirm: "Remettre la bouteille de {name} en stock ? Rien n'en a été versé, elle retourne dans le stock.",
    summary: {
      title: "Bouteille terminée",
      doses: "Doses vendues",
      lasted: "Durée",
      minutes: { one: "{count} minute", other: "{count} minutes" },
      hours: { one: "{count} heure", other: "{count} heures" },
      days: { one: "{count} jour", other: "{count} jours" },
      revenue: "Recette",
      cost: "Coût de la bouteille",
      margin: "Reste après la bouteille",
      unknown: "inconnu",
      note: "Les doses remboursées ne sont pas comptées.",
      done: "OK",
    },
    finishing: "Fermeture…",
    openTitle: "Ouvrir une bouteille",
    openHint: "Une bouteille sort du stock maintenant. Les doses sont ensuite vendues depuis elle jusqu'à ce que vous la marquiez terminée.",
    open: "Ouvrir",
    opening: "Ouverture…",
    left: { one: "{count} restante", other: "{count} restantes" },
    noneLeft: "plus en stock",
    noProducts: "Aucun produit n'est encore vendu à la dose. Dans Produits, appuyez sur « Vente à la dose » pour cocher vos liquides et donner le prix d'une dose.",
    failed: "Cela n'a pas fonctionné. Réessayez.",
    product: {
      toggle: "Aussi vendu à la dose",
      hint: "Pour un liquide versé dans le pod du client depuis une bouteille ouverte. Ouvrez une bouteille à la caisse (elle sort du stock), puis vendez-en des doses.",
      price: "Prix d'une dose",
      perBottle: "Doses par bouteille jusqu'ici : environ {count}",
    },
    setup: {
      button: "Vente à la dose",
      title: "Liquides vendus à la dose",
      intro: "Cochez chaque liquide que vous versez à la dose et indiquez le prix d'une dose, puis enregistrez une seule fois. Décocher un liquide garde son prix pour la prochaine fois.",
      search: "Chercher un liquide",
      priceForAll: "Prix de la dose",
      applyToTicked: "Appliquer aux cochés",
      tickAll: "Cocher tout l'affiché",
      untickAll: "Décocher tout l'affiché",
      ticked: { one: "{count} coché", other: "{count} cochés" },
      bottle: "Bouteille : {price}",
      dosePrice: "Dose",
      empty: "Aucun liquide dans la boutique pour l'instant.",
      missingPrice: { one: "{count} liquide coché n'a pas de prix de dose", other: "{count} liquides cochés n'ont pas de prix de dose" },
      save: { one: "Enregistrer {count} modification", other: "Enregistrer {count} modifications" },
      nothing: "Rien à enregistrer",
      saving: "Enregistrement…",
      saved: { one: "{count} liquide enregistré.", other: "{count} liquides enregistrés." },
      failed: "Cela n'a pas fonctionné. Réessayez.",
    },
    menu: {
      toggle: "Indiquer sur le menu les liquides vendus à la dose",
      hint: "Les clients voient « aussi à la dose » et le prix d'une dose sur ces produits.",
    },
  },

  ar: {
    button: "جرعات",
    title: "البيع بالجرعة",
    subtitle: "المس قارورة مفتوحة لإضافة جرعة إلى التذكرة.",
    openBottles: "القوارير المفتوحة",
    noneOpen: "لا توجد قارورة مفتوحة. افتح واحدة أدناه — تُخصم من المخزون.",
    dose: "جرعة",
    lineName: "{name} · جرعة",
    sold: {
      zero: "لم تُبع أي جرعة",
      one: "بيعت جرعة واحدة",
      two: "بيعت جرعتان",
      few: "بيعت {count} جرعات",
      many: "بيعت {count} جرعة",
      other: "بيعت {count} جرعة",
    },
    perBottle: "حوالي {count} في القارورة",
    added: {
      zero: "لا شيء في التذكرة",
      one: "واحدة في التذكرة",
      two: "اثنتان في التذكرة",
      few: "{count} في التذكرة",
      many: "{count} في التذكرة",
      other: "{count} في التذكرة",
    },
    finish: "انتهت القارورة",
    finishConfirm: "هل فرغت قارورة {name}؟ بيعت منها {count} جرعة.",
    cancel: "إرجاع للمخزون",
    cancelConfirm: "إرجاع قارورة {name} إلى المخزون؟ لم يُصبّ منها شيء، فتعود إلى المخزون.",
    summary: {
      title: "انتهت القارورة",
      doses: "الجرعات المباعة",
      lasted: "المدة",
      minutes: {
        zero: "أقل من دقيقة",
        one: "دقيقة واحدة",
        two: "دقيقتان",
        few: "{count} دقائق",
        many: "{count} دقيقة",
        other: "{count} دقيقة",
      },
      hours: {
        zero: "أقل من ساعة",
        one: "ساعة واحدة",
        two: "ساعتان",
        few: "{count} ساعات",
        many: "{count} ساعة",
        other: "{count} ساعة",
      },
      days: {
        zero: "أقل من يوم",
        one: "يوم واحد",
        two: "يومان",
        few: "{count} أيام",
        many: "{count} يومًا",
        other: "{count} يوم",
      },
      revenue: "الإيراد",
      cost: "تكلفة القارورة",
      margin: "المتبقي بعد القارورة",
      unknown: "غير معروف",
      note: "الجرعات المسترجعة لا تُحتسب.",
      done: "حسنًا",
    },
    finishing: "جارٍ الإغلاق…",
    openTitle: "فتح قارورة",
    openHint: "تُخصم قارورة من المخزون الآن. ثم تُباع منها الجرعات حتى تعلّمها منتهية.",
    open: "فتح",
    opening: "جارٍ الفتح…",
    left: {
      zero: "لم يبق شيء",
      one: "بقيت واحدة",
      two: "بقيت اثنتان",
      few: "بقيت {count}",
      many: "بقيت {count}",
      other: "بقيت {count}",
    },
    noneLeft: "نفد المخزون",
    noProducts: "لا يوجد منتج يُباع بالجرعة بعد. في المنتجات، اضغط «البيع بالجرعة» لتحديد سوائلك وسعر الجرعة.",
    failed: "لم ينجح ذلك. أعد المحاولة.",
    product: {
      toggle: "يُباع أيضًا بالجرعة",
      hint: "لسائل تصبّه في جهاز الزبون من قارورة مفتوحة. افتح قارورة في الصندوق (تُخصم من المخزون) ثم بِع منها جرعات.",
      price: "سعر الجرعة",
      perBottle: "عدد الجرعات في القارورة حتى الآن: حوالي {count}",
    },
    setup: {
      button: "البيع بالجرعة",
      title: "السوائل المباعة بالجرعة",
      intro: "حدّد كل سائل تبيعه بالجرعة واكتب سعر الجرعة، ثم احفظ مرة واحدة. إلغاء التحديد يحتفظ بالسعر للمرة القادمة.",
      search: "ابحث عن سائل",
      priceForAll: "سعر الجرعة",
      applyToTicked: "طبّقه على المحدّدة",
      tickAll: "حدّد كل المعروض",
      untickAll: "ألغِ تحديد المعروض",
      ticked: {
        zero: "لا شيء محدّد",
        one: "سائل واحد محدّد",
        two: "سائلان محدّدان",
        few: "{count} سوائل محدّدة",
        many: "{count} سائلًا محدّدًا",
        other: "{count} سائل محدّد",
      },
      bottle: "القارورة: {price}",
      dosePrice: "الجرعة",
      empty: "لا يوجد سائل في المتجر بعد.",
      missingPrice: {
        zero: "كل السوائل المحدّدة لها سعر جرعة",
        one: "سائل محدّد بلا سعر جرعة",
        two: "سائلان محدّدان بلا سعر جرعة",
        few: "{count} سوائل محدّدة بلا سعر جرعة",
        many: "{count} سائلًا محدّدًا بلا سعر جرعة",
        other: "{count} سائل محدّد بلا سعر جرعة",
      },
      save: {
        zero: "لا شيء للحفظ",
        one: "حفظ تعديل واحد",
        two: "حفظ تعديلين",
        few: "حفظ {count} تعديلات",
        many: "حفظ {count} تعديلًا",
        other: "حفظ {count} تعديل",
      },
      nothing: "لا شيء للحفظ",
      saving: "جارٍ الحفظ…",
      saved: {
        zero: "لم يتغيّر شيء.",
        one: "حُفظ سائل واحد.",
        two: "حُفظ سائلان.",
        few: "حُفظت {count} سوائل.",
        many: "حُفظ {count} سائلًا.",
        other: "حُفظ {count} سائل.",
      },
      failed: "لم ينجح ذلك. أعد المحاولة.",
    },
    menu: {
      toggle: "اذكر في القائمة السوائل التي تُباع بالجرعة",
      hint: "يرى الزبائن «متوفر أيضًا بالجرعة» وسعر الجرعة على هذه المنتجات.",
    },
  },
};

export default doses;
